import { CategoriesHelpers } from 'idt-ecommerce-api/catalog/categories/categories.helpers';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { CategoryLevel } from '../models/brand-analytics.models';
import { AnalyticsComponentEvent, AnalyticsComponentProduct } from 'idt-analytics-api/models';
import { AnalyticsSettingsEvent } from 'analytics-api';

export class AnalyticsHelpersOysho {

  /**
   * Transforma el path de categorías de un producto al formato que espera recibir analítica
   * @param path: Path de categorías de un producto
   * @returns Path de categorías de un producto transformado
   */
   public static parsePath(path: string): string {
    const dividedPath = path.split('/');
    dividedPath.unshift('woman');

    return dividedPath.join('/').toLowerCase();
  }

  /**
   * Construye y devuelve el partnumber de un producto
   * @param product: Producto del que se quiere construir el partnumber
   * @param extras: Objeto con dos booleans: color y talla. Indica si se desea introducir o no cada uno de dichos atributos en el partnumber.
   * @returns Partnumber del producto
   */
  public static getPartnumber(product: AnalyticsComponentProduct, extras: {color?: boolean; size?: boolean}): string | undefined {
    if (product.gridElemType === 'BUNDLE') { return product.fullReference; }
    const dividedReference = product.fullReference?.split('-');
    const season = dividedReference?.[1];
    const partnumber = dividedReference?.[0];
    const color = product.color || product.colors[0].id;
    let info = '';
    if (!isDefined(season) || !isDefined(partnumber) || (extras.color && !isDefined(color)) || (extras.size && !isDefined(product.sizeCode))) {
      return undefined;
    }
    if (extras.color) {
      info += color;
    }
    if (extras.size) {
      info += product.sizeCode;
    }
    return `${partnumber}${info}-${season}`;
  }

  /**
   * Obtiene y devuelve el nivel de categoría del producto que se solicite como parámetro
   * @param categoryType: Nivel de categoría del producto a obtener.
   * @param context: Contexto de ecommerce
   * @param componentData: Información de analítica del componente
   * @returns Categoría del producto solicitada
   */
   public static getCategory(categoryType: CategoryLevel, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): string | undefined {
    if (context?.categories && componentData?.categoryId) {
      const flatCategories = CategoriesHelpers.flatCategoriesMap(context.categories);
      const categoriesPath = flatCategories[componentData.categoryId]?.sectionPathEn;
      const categories = categoriesPath ? this.parsePath(categoriesPath).split('/') : undefined;
      if (categories?.length) {
        switch (categoryType) {
          case 'category': return 'woman';
          case 'subcategory': return categories[1];
          case 'subsubcategory': return categories[2];
          case 'subsubsubcategory': return categories[3];
        }
      }
    }
    return undefined;
  }

  /**
   * Crea la configuración de analítica de un componente rellenando sus acciones con los eventos relacionados correspondientes
   * @param events: Eventos de analítica rellenados en la tab por el usuario
   * @param analytics: Objeto vacío a rellenar con los eventos relacionados correspondientes a cada acción
   * @param component: Nombre del componente hijo o común para el que se construye la analítica, en caso de que existiera
   * @returns Objeto introducido en analytics con cada acción rellena con su evento relacionado
   */
  public static setComponentAnalyticsSettings<T>(events: AnalyticsSettingsEvent[], analytics: T, component?: string): T {
    return events.reduce((acc, value) => {
      const eventParts = value.name.split('_');
      const camelProp = this.kebabCaseToCamelCase(eventParts[eventParts.length - 1]);
      const hasNoChildComponent = !isDefined(component) && ((eventParts[0] === 'mobile' && eventParts.length === 2) || eventParts.length === 1);
      if ((isDefined(component) && value.name.includes(component)) || hasNoChildComponent) {
        acc = { ...acc, [camelProp]: value.relatedEvent };
      }
      return acc;
    }, analytics);
  }

  /**
   * Transforma un string de kebab-case a camelCase
   * @param value: string en kebab-case a transformar
   */
  private static kebabCaseToCamelCase(value: string): string {
    return value.replace(/([-][a-z])/g, ($1) => {
      return $1.toUpperCase().replace('-', '');
    });
  }

}
